// PostHog Cloud EU lazy-init wrapper.
// Bundle cost is zero until consent.A flips true; SDK is dynamically imported
// inside requestIdleCallback so it never lands in the critical path.
//
// Pseudonymous mode: person_profiles='identified_only'. identifyUser() is
// called with the Supabase user UUID after login (Bucket A consent covers it);
// when it lands before the SDK initialises, which is every signed-in page
// load, the UUID is the SDK's bootstrap id, so one person is one distinct id.
// PostHog never sees email/name; the UUID is opaque to it. Gives accurate
// MAU/retention without storing PII. resetIdentity() on signout rotates
// distinct_id back to anonymous.

import type { PostHog } from 'posthog-js';
import { validateEvent, type EventName, type EventPropsMap } from './events';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
// Reverse-proxied through Next.js rewrites in next.config.mjs so ad-blockers
// can't kill telemetry. Path `/relay` is intentionally non-obvious (avoid
// /ingest, /analytics, /telemetry — blockers match those).
const POSTHOG_HOST = '/relay';
const POSTHOG_UI_HOST = 'https://eu.posthog.com';

let client: PostHog | null = null;
let initPromise: Promise<PostHog | null> | null = null;
let pendingQueue: Array<{ name: EventName; props: object }> = [];
// Identity action waiting for client init. `undefined` = no action, a string
// = identify with that id, `null` = reset to anonymous. A string present when
// the SDK initialises becomes its bootstrap id; anything else is applied in
// `loaded:`.
let pendingIdentity: string | null | undefined = undefined;
let revoked = false;
// True from the moment consent.A turns telemetry on until it is revoked. The
// queue below exists to cover the gap between that decision and the SDK
// finishing its lazy import, NOT to bank activity from before the user
// consented: events tracked while this is false are dropped, so granting
// consent later in a session cannot retroactively flush what the user did
// before granting it.
let started = false;
// Bumped by every start-up initTelemetry() schedules and by shutdownTelemetry().
// A scheduled start-up runs only while its number is still the current one.
let startupGeneration = 0;

function isConfigured(): boolean {
  // Skip telemetry in non-production builds so dev/preview noise never lands
  // in the prod project. Set NEXT_PUBLIC_POSTHOG_DEV=true to opt in locally
  // when actively QA-ing tracking.
  if (
    process.env.NODE_ENV !== 'production' &&
    process.env.NEXT_PUBLIC_POSTHOG_DEV !== 'true'
  ) {
    return false;
  }
  return Boolean(POSTHOG_KEY && POSTHOG_KEY.length > 0);
}

function scheduleIdle(fn: () => void) {
  if (typeof window === 'undefined') return;
  type IdleWindow = Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
  };
  const w = window as IdleWindow;
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(fn, { timeout: 2000 });
  } else {
    setTimeout(fn, 0);
  }
}

export async function initTelemetry(): Promise<PostHog | null> {
  if (typeof window === 'undefined') return null;
  if (client) return client;
  if (initPromise) return initPromise;
  if (!isConfigured()) {
    if (process.env.NODE_ENV !== 'production') {
      const hasKey = Boolean(POSTHOG_KEY && POSTHOG_KEY.length > 0);
      console.warn(
        hasKey
          ? '[telemetry] disabled in dev. Set NEXT_PUBLIC_POSTHOG_DEV=true to enable.'
          : '[telemetry] NEXT_PUBLIC_POSTHOG_KEY not set; telemetry disabled.',
      );
    }
    return null;
  }
  revoked = false;
  started = true;
  const generation = ++startupGeneration;

  initPromise = new Promise((resolve) => {
    scheduleIdle(async () => {
      // A session revoked before its idle callback ran must not start the SDK:
      // not after a plain revoke (consent is off), and not after a revoke and
      // re-grant either, where the new session's own callback starts it. The
      // SDK's init only works once per page, so a stale callback that got
      // there first would leave the live session's start-up never resolving.
      const stale = () => generation !== startupGeneration;
      if (stale()) { resolve(null); return; }
      try {
        const mod = await import('posthog-js');
        if (stale()) { resolve(null); return; }
        const ph = mod.default;
        // Read at init, not when initTelemetry() was called: TelemetryGate is a
        // child of ConsentProvider, so its effect calls initTelemetry() before
        // the provider's effect calls identifyUser(). Both run in the same
        // commit, before this idle callback, so the id is here by now. Taking
        // it as the bootstrap id means the SDK starts as that identified user,
        // so no anonymous id is minted and then merged into the person by an
        // identify call: one more distinct id per page load until now.
        // If the id arrives later after all, `loaded:` identifies as before.
        const bootstrapId = typeof pendingIdentity === 'string' ? pendingIdentity : undefined;
        if (bootstrapId !== undefined) pendingIdentity = undefined;
        ph.init(POSTHOG_KEY!, {
          api_host: POSTHOG_HOST,
          ui_host: POSTHOG_UI_HOST,
          person_profiles: 'identified_only',
          // Memory-only persistence honours the "no cookies" promise on
          // the public privacy page. Nothing survives the page, so without
          // the bootstrap id below the SDK would start anonymous on every load.
          persistence: 'memory',
          ...(bootstrapId !== undefined
            ? { bootstrap: { distinctID: bootstrapId, isIdentifiedID: true } }
            : {}),
          capture_pageview: true,
          capture_pageleave: true,
          autocapture: false,
          // Stack traces only, no DOM/user content. Consistent with the
          // "no content" posture and the only privacy-safe autocapture.
          capture_exceptions: true,
          disable_session_recording: true,
          disable_surveys: true,
          ip: false,
          respect_dnt: true,
          loaded: () => {
            if (revoked) {
              try { ph.opt_out_capturing(); ph.reset(); } catch { /* noop */ }
              pendingQueue = [];
              resolve(null);
              return;
            }
            client = ph;
            if (pendingIdentity !== undefined) {
              try {
                if (pendingIdentity === null) ph.reset();
                else ph.identify(pendingIdentity);
              } catch { /* noop */ }
              pendingIdentity = undefined;
            }
            for (const e of pendingQueue) {
              try { ph.capture(e.name, e.props); } catch { /* noop */ }
            }
            pendingQueue = [];
            resolve(ph);
          },
        });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[telemetry] init failed', err);
        }
        resolve(null);
      }
    });
  });

  return initPromise;
}

// Tear down: stop sending, drop reference. Called when consent.A flips false.
export function shutdownTelemetry(): void {
  revoked = true;
  started = false;
  startupGeneration++;
  pendingQueue = [];
  pendingIdentity = undefined;
  const c = client;
  client = null;
  initPromise = null;
  if (!c) return;
  try {
    c.opt_out_capturing();
    c.reset();
  } catch { /* noop */ }
}

// Pseudonymous identify with the Supabase user UUID. Privacy-safe: the UUID
// is opaque to PostHog. Lets MAU/retention reflect real humans across
// tab/session boundaries. Queues until client init resolves.
export function identifyUser(distinctId: string): void {
  if (revoked) return;
  const c = client;
  if (c) {
    try { c.identify(distinctId); } catch { /* noop */ }
    return;
  }
  pendingIdentity = distinctId;
}

// Roll distinct_id back to a fresh anonymous one. Called on signout so the
// next user on the same browser isn't merged with the previous account.
export function resetIdentity(): void {
  pendingIdentity = null;
  const c = client;
  if (!c) return;
  try { c.reset(); } catch { /* noop */ }
}

// Type-safe capture. No-op until init resolves; queues until ready.
export function track<E extends EventName>(name: E, props: EventPropsMap[E]): void {
  if (revoked) return;
  const validated = validateEvent(name, props);
  if (!validated) return;
  // Snapshot client to a local so a concurrent shutdown can't null it mid-call.
  const c = client;
  if (c) {
    try { c.capture(name, validated as object); } catch { /* noop */ }
    return;
  }
  if (!started) return;
  pendingQueue.push({ name, props: validated as object });
  if (pendingQueue.length > 50) pendingQueue.shift();
}

export function isTelemetryActive(): boolean {
  return Boolean(client);
}
